import React, { useCallback, useEffect, useRef, useState } from "react";
import { TrendingUp, X, Activity, RefreshCw, CheckCircle2, ShieldCheck } from "lucide-react";
import api, { money } from "@/lib/api";

const COINS = [{ k: "btc", label: "BTC" }, { k: "eth", label: "ETH" }, { k: "gold", label: "XAU" }];
const TFS = [{ d: 1, label: "1D" }, { d: 7, label: "7D" }, { d: 30, label: "30D" }];

function Candles({ candles }) {
  if (!candles?.length) return <div className="grid h-[180px] place-items-center text-sm text-white/50">No chart data</div>;
  const W = 560, H = 150, pad = 6;
  const hi = Math.max(...candles.map((c) => c.h));
  const lo = Math.min(...candles.map((c) => c.l));
  const span = hi - lo || 1;
  const cw = (W - pad * 2) / candles.length;
  const y = (v) => pad + (H - pad * 2) * (1 - (v - lo) / span);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
      {candles.map((c, i) => {
        const x = pad + i * cw + cw / 2;
        const up = c.c >= c.o;
        const col = up ? "#22c55e" : "#ef4444";
        const bt = y(Math.max(c.o, c.c));
        const bb = y(Math.min(c.o, c.c));
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={y(c.h)} y2={y(c.l)} stroke={col} strokeWidth="1" />
            <rect x={x - Math.max(1, cw * 0.3)} y={bt} width={Math.max(2, cw * 0.6)} height={Math.max(1, bb - bt)} fill={col} />
          </g>
        );
      })}
    </svg>
  );
}

export default function InvestTradingModal({ open, amount, busy, memberId, onCancel, onConfirm }) {
  const [step, setStep] = useState(0);
  const [prices, setPrices] = useState(null);
  const [pLoading, setPLoading] = useState(false);
  const [coin, setCoin] = useState("btc");
  const [tf, setTf] = useState(1);
  const [ohlc, setOhlc] = useState(null);
  const [oLoading, setOLoading] = useState(false);
  const timer = useRef();

  const loadPrices = useCallback(async () => {
    setPLoading(true);
    try { const r = await api.get("/market/prices"); setPrices(r.data); }
    catch { setPrices({ items: [], available: false }); }
    finally { setPLoading(false); }
  }, []);
  const loadOhlc = useCallback(async (c, d) => {
    setOLoading(true);
    try { const r = await api.get(`/market/ohlc/${c}?days=${d}`); setOhlc(r.data); }
    catch { setOhlc({ candles: [], available: false }); }
    finally { setOLoading(false); }
  }, []);

  useEffect(() => { if (!open) setStep(0); }, [open]);
  useEffect(() => {
    if (open && step === 1) {
      loadPrices();
      timer.current = setInterval(loadPrices, 20000);
      return () => clearInterval(timer.current);
    }
  }, [open, step, loadPrices]);
  useEffect(() => { if (open && step === 1) loadOhlc(coin, tf); }, [coin, tf, open, step, loadOhlc]);

  if (!open) return null;
  const card = { background: "linear-gradient(160deg,#0f1613,#0a0f0c)", border: "1px solid rgba(34,197,94,0.3)", boxShadow: "0 24px 60px -20px rgba(0,0,0,0.7)" };
  const tile = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center overflow-hidden bg-black/80 p-4" data-testid="invest-trading-modal">
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-2xl overflow-y-auto rounded-2xl p-6 [scrollbar-width:thin]" style={card}>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: "rgba(34,197,94,0.16)" }}><TrendingUp size={18} className="text-emerald-400" /></div>
            <div>
              <h3 className="font-display text-lg font-bold leading-tight text-white">{step === 0 ? "Account Activation" : "Live Market Monitor"}</h3>
              <p className="text-[11px] text-white/55">ALEXA FINTECH</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-white/60 hover:text-white" data-testid="invest-cancel"><X size={18} /></button>
        </div>

        {step === 0 && (
          <div data-testid="invest-step-activation">
            <div className="rounded-xl p-5" style={tile}>
              {[["Member ID", memberId], ["Selected Package", `$${money(amount)}`], ["Investment Amount", `$${money(amount)}`], ["Status", "Pending activation"]].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-white/5 py-2.5 last:border-0">
                  <span className="text-sm text-white/60">{k}</span>
                  <span className="text-sm font-bold text-white">{v || "—"}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-center text-[11px] text-white/45">This is an account/package activation that allocates your package to the daily ROI program — not a live broker trade.</p>
            <button onClick={() => setStep(1)} disabled={busy || !amount}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl btn3d btn3d-yellow py-3.5 text-base font-extrabold disabled:opacity-60" data-testid="invest-activate-account-btn">
              <TrendingUp size={18} /> ACTIVATE ACCOUNT
            </button>
          </div>
        )}

        {step === 1 && (
          <div data-testid="invest-step-market">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold text-emerald-300" style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.4)" }} data-testid="market-status">
                <Activity size={12} /> MARKET MONITORING ACTIVE
              </span>
              <span className="flex items-center gap-2 text-[11px] text-white/50">
                <button onClick={loadPrices} className="hover:text-white" title="Refresh"><RefreshCw size={13} className={pLoading ? "animate-spin" : ""} /></button>
                {prices?.updated_at ? `Updated ${new Date(prices.updated_at).toLocaleTimeString()}` : "—"}
              </span>
            </div>

            {prices && !prices.available ? (
              <div className="rounded-xl p-6 text-center text-sm text-amber-300" style={tile} data-testid="market-unavailable">Market data temporarily unavailable</div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {(prices?.items || (pLoading ? Array.from({ length: 5 }) : [])).map((t, i) => {
                  if (!t) return <div key={i} className="h-14 animate-pulse rounded-lg bg-white/5" />;
                  const dp = ["EUR/USD", "GBP/USD"].includes(t.symbol) ? 4 : 2;
                  return (
                    <div key={t.symbol} className="rounded-lg px-3 py-2" style={tile} data-testid={`market-tick-${t.symbol}`}>
                      <p className="text-[10px] text-white/55">{t.symbol}</p>
                      <p className="font-mono text-sm font-bold text-white">{Number(t.price).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })}</p>
                      <p className={`text-[10px] font-semibold ${t.change == null ? "text-white/40" : t.change >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{t.change == null ? "—" : `${t.change >= 0 ? "▲" : "▼"} ${Math.abs(t.change).toFixed(2)}%`}</p>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-4 flex items-center justify-between gap-2">
              <div className="flex gap-1">
                {COINS.map((c) => (
                  <button key={c.k} onClick={() => setCoin(c.k)} className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${coin === c.k ? "bg-emerald-500 text-black" : "bg-white/5 text-white/70"}`} data-testid={`market-coin-${c.k}`}>{c.label}</button>
                ))}
              </div>
              <div className="flex gap-1">
                {TFS.map((t) => (
                  <button key={t.d} onClick={() => setTf(t.d)} className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${tf === t.d ? "bg-emerald-500 text-black" : "bg-white/5 text-white/70"}`} data-testid={`market-tf-${t.label}`}>{t.label}</button>
                ))}
              </div>
            </div>
            <div className="mt-2 overflow-hidden rounded-xl" style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.08)" }}>
              {oLoading ? <div className="grid h-[180px] place-items-center text-white/50"><RefreshCw size={18} className="animate-spin" /></div>
                : ohlc && !ohlc.available ? <div className="grid h-[180px] place-items-center text-sm text-amber-300">Chart data temporarily unavailable</div>
                : <Candles candles={ohlc?.candles} />}
            </div>

            <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-[11px] text-white/45"><ShieldCheck size={12} /> Live market monitoring only — no live broker/exchange auto-execution is connected.</p>
            <button onClick={onConfirm} disabled={busy}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl btn3d btn3d-yellow py-3.5 text-base font-extrabold disabled:opacity-60" data-testid="invest-confirm-btn">
              <CheckCircle2 size={18} /> {busy ? "Activating…" : `Confirm & Activate $${money(amount)}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
